import { z } from 'zod'
import {
  CRM_ADMIN_READ_CONTRACT_VERSION,
  crmAdminUserSummarySchema,
  crmIntegrationSummarySchema,
} from './crm-admin-read'

/**
 * Request schemas intentionally preserve the handwritten validator boundary.
 * Existing handlers own field precedence, validation messages, auth ordering,
 * and the secret-safe integration policy.
 */
const optionalUnknown = z.unknown().optional()

export const crmAdminUserCreateRequestSchema = z.object({
  email: optionalUnknown,
  name: optionalUnknown,
  full_name: optionalUnknown,
  role: optionalUnknown,
  active: optionalUnknown,
}).passthrough()

export const crmAdminUserPatchRequestSchema = z.object({
  email: optionalUnknown,
  name: optionalUnknown,
  full_name: optionalUnknown,
  role: optionalUnknown,
  active: optionalUnknown,
}).passthrough()

export const crmAdminIntegrationPatchRequestSchema = z.object({
  enabled: optionalUnknown,
  rateLimitPerMinute: optionalUnknown,
  rate_limit_per_minute: optionalUnknown,
  secretRequired: optionalUnknown,
  secret_required: optionalUnknown,
  sharedSecret: optionalUnknown,
  shared_secret: optionalUnknown,
}).passthrough()

export const crmAdminWriteSideEffectsSchema = z.object({
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  billingTouched: z.literal(false),
  eventEmitted: z.literal(false),
  uploadTouched: z.literal(false),
  llmTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmAdminMetadataEvidenceSchema = z.object({
  db1TableRead: z.literal('crm_ingest_sources'),
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  rawSecretRead: z.literal(false),
  rawSecretWritten: z.literal(false),
  credentialPointerReturned: z.literal(false),
  providerCalled: z.literal(false),
  xanoTouched: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmAdminUserMutationResponseSchema = z.object({
  contract_version: z.literal(CRM_ADMIN_READ_CONTRACT_VERSION),
  user: crmAdminUserSummarySchema,
  outcome: z.enum(['created', 'reused', 'restored']),
  identityOutcome: z.enum(['created', 'reused']),
  membershipOutcome: z.enum(['created', 'reused', 'restored']),
  sideEffects: crmAdminWriteSideEffectsSchema,
}).passthrough()

const crmAdminIntegrationWriteSideEffectsSchema = z.object({
  db1Read: z.boolean(),
  db1Written: z.boolean(),
  auditWritten: z.boolean(),
  rawSecretRead: z.literal(false),
  rawSecretWritten: z.literal(false),
  providerCalled: z.literal(false),
  queuePublished: z.literal(false),
  db2Touched: z.literal(false),
  deployLiveTraffic: z.literal(false),
  destructiveCleanup: z.literal(false),
}).passthrough()

export const crmAdminIntegrationResponseSchema = z.object({
  contract_version: z.literal(CRM_ADMIN_READ_CONTRACT_VERSION),
  integration: crmIntegrationSummarySchema,
  metadataEvidence: crmAdminMetadataEvidenceSchema,
  sideEffects: crmAdminIntegrationWriteSideEffectsSchema,
}).passthrough()

export const CRM_ADMIN_WRITE_ERROR_CODES = [
  'CR_VALIDATION_FAILED',
  'CR_PERMISSION_DENIED',
  'CR_AUTH_UNAVAILABLE',
  'CR_MEMBERSHIP_UNAVAILABLE',
  'CR_DOMAIN_UNAVAILABLE',
] as const

export const crmAdminWriteErrorEnvelopeSchema = z.object({
  contract_version: z.string().min(1),
  error: z.object({
    code: z.enum(CRM_ADMIN_WRITE_ERROR_CODES),
    message: z.string().min(1),
    reference: z.string().min(1),
  }).passthrough(),
}).passthrough()

export type CrmAdminUserCreateRequest = z.infer<typeof crmAdminUserCreateRequestSchema>
export type CrmAdminUserPatchRequest = z.infer<typeof crmAdminUserPatchRequestSchema>
export type CrmAdminIntegrationPatchRequest = z.infer<typeof crmAdminIntegrationPatchRequestSchema>
export type CrmAdminWriteSideEffects = z.infer<typeof crmAdminWriteSideEffectsSchema>
export type CrmAdminMetadataEvidence = z.infer<typeof crmAdminMetadataEvidenceSchema>
export type CrmAdminUserMutationResponse = z.infer<typeof crmAdminUserMutationResponseSchema>
export type CrmAdminIntegrationResponse = z.infer<typeof crmAdminIntegrationResponseSchema>
export type CrmAdminWriteErrorCode = (typeof CRM_ADMIN_WRITE_ERROR_CODES)[number]
export type CrmAdminWriteErrorEnvelope = z.infer<typeof crmAdminWriteErrorEnvelopeSchema>
